import {useEffect, useState} from "react";
import moment from "moment";
import "moment-timezone";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Form from "react-bootstrap/Form";

import DurationDisplay from "components/datestimes/DurationDisplay";
import FormRouteStatus from "./FormRouteStatus";
import {useTranslation} from "react-i18next";
import {REQUIRES_FIELDS} from "site-apps/distribution/route/RouteRequires";
import {RouteReadOnlyStatus} from "site-apps/distribution/route/RouteModel";
import SearchSelect from "components/selects/SearchSelect";

const FormRouteInfo = ({formData, waypoints, hasChanged, onClickCalculate, onChangeDriver, onChangeTruck, onChangeRequires, requiresReadOnly = true, readOnly = false}) => {
    const {t: _Tc} = useTranslation("choices");
    const {t: _Tr} = useTranslation("routes");
    const {t: _Tt} = useTranslation("tables");
    const {t: _Tf} = useTranslation("forms");
    const [urlGMaps, setUrlGMaps] = useState("");
    const styleLabel = {
        maxWidth: "100px",
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
    };
    const styleValue = {
        fontSize: "1.1rem",
        fontWeight: "400",
    };
    const [statusForm, setStatusForm] = useState("r");

    // primera opción "Todos": sin conductor, la ruta la ven todos los conductores en la app de cabina
    const driverOptions = [
        {value: "", label: _Tf("labels.all")},
        ...(formData.available_drivers ?? []).map((d) => ({value: d.id, label: d.email})),
    ];
    const driverValue = driverOptions.find((o) => o.value === (formData.driver?.id ?? "")) ?? driverOptions[0];

    useEffect(() => {
        // sesión de cabina en curso o hecha: solo lectura, sea cual sea la fecha
        if (RouteReadOnlyStatus.includes(formData.unload_status)) {
            setStatusForm("r");
            return;
        }
        let now = moment().startOf("day");
        let dated = formData.created_at ? moment(formData.created_at).startOf("day") : null;
        if (!formData.created_at) {
            setStatusForm("r");
        } else if (dated.isBefore(now)) {
            setStatusForm("r");
        } else if (dated.isSame(now)) {
            setStatusForm("w");
        } else if (dated.isAfter(now)) {
            if (formData.id) {
                setStatusForm("w");
            } else {
                setStatusForm("c");
            }
        }
    }, [formData]);

    useEffect(() => {
        if (waypoints.length) {
            let origin = waypoints[0];
            let checkpoints = waypoints.slice(1);
            let url = buildCircularRouteURL(origin, checkpoints);
            setUrlGMaps(url);
        }
    }, [waypoints]);

    const buildCircularRouteURL = (origin, waypoints) => {
        let baseURL = "https://www.google.com/maps/dir/?api=1";
        let encodedOrigin = `${encodeURIComponent(origin.lat)},${encodeURIComponent(origin.lng)}`;
        let encodedWaypoints = waypoints
            .map((point) => `${encodeURIComponent(point.lat)},${encodeURIComponent(point.lng)}`)
            .join("%7C");
        return `${baseURL}&origin=${encodedOrigin}&destination=${encodedOrigin}&waypoints=${encodedWaypoints}`;
    };

    return (
        <div className="row mb-4 border-bottom">
            <div className="col-12">
                <table className="table table-borderless">
                    <tbody>
                        <tr>
                            <td className="p-0">
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tt("common.created")}:
                                    </div>
                                    <div className="col" style={styleValue}>
                                        {formData.created_at ? formData.created_at.format("DD-MM-YYYY HH:mm") : <></>}
                                    </div>
                                </div>
                            </td>
                            <td className="p-0">
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tr("labels.programming")}:
                                    </div>
                                    <div className="col" style={styleValue}>
                                        {formData.prog_name}
                                    </div>
                                </div>
                            </td>
                            <td
                                className="ps-3 pe-3"
                                rowSpan={3}
                                style={{verticalAlign: "middle", borderLeft: "1px solid #dee2e6"}}>
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tt("users.driver")}:
                                    </div>
                                    <div className="col" style={{display: "flex", flexWrap: "wrap", alignItems: "center"}}>
                                        {/* Sin conductor: la ruta la ven todos los conductores en la app de cabina */}
                                        <div style={{width: "100%"}}>
                                            <SearchSelect
                                                options={driverOptions}
                                                value={driverValue}
                                                isDisabled={readOnly}
                                                placeholder={_Tf("labels.all")}
                                                onChange={(option) =>
                                                    onChangeDriver &&
                                                    onChangeDriver(option?.value ? Number(option.value) : 0)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tt("fleet.truck")}:
                                    </div>
                                    <div className="col" style={{display: "flex", flexWrap: "wrap", alignItems: "center"}}>
                                        <select
                                            className="form-select "
                                            style={{maxWidth: "100%"}}
                                            value={formData.truck?.id ?? ""}
                                            disabled={readOnly}
                                            onChange={(e) => onChangeTruck && onChangeTruck(Number(e.target.value))}>
                                            <option value="">{_Tt("devices.None")}</option>
                                            {(formData.available_trucks ?? []).map((t) => (
                                                <option key={t.id} value={t.id}>
                                                    {t.plate}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tt("fleet.requires")}:
                                    </div>
                                    <div className="col" style={{display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center"}}>
                                        {REQUIRES_FIELDS.map(({key, label}) => (
                                            <div
                                                key={key}
                                                className="d-flex align-items-center gap-1"
                                                title={label}>
                                                <small className="text-muted">{label}</small>
                                                <Form.Check
                                                    type="switch"
                                                    checked={!!(formData.requires || {})[key]}
                                                    disabled={requiresReadOnly}
                                                    readOnly={requiresReadOnly}
                                                    onChange={(e) => onChangeRequires && onChangeRequires(key, e.target.checked)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {requiresReadOnly && !!formData.opened_at && (
                                    <div className="row">
                                        <div className="col small text-secondary" style={styleLabel}></div>
                                        <div className="col small text-muted">
                                            {_Tt("fleet.requires_opened")}
                                        </div>
                                    </div>
                                )}
                            </td>
                            <td className="pt-0 ps-0 pb-0 pe-3 text-end">
                                <FormRouteStatus stat={statusForm} />
                            </td>
                        </tr>
                        <tr>
                            <td className="p-0">
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tt("programming.name")}:
                                    </div>
                                    <div className="col" style={styleValue}>
                                        {formData.name}
                                    </div>
                                </div>
                            </td>
                            <td className="p-0">
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        URL Gmaps:
                                    </div>
                                    <div className="col" style={styleValue}>
                                        <a rel="noreferrer" href={urlGMaps} target="_blank">
                                            {_Tr("labels.browser")} <FontAwesomeIcon icon="up-right-from-square" />
                                        </a>
                                    </div>
                                </div>
                            </td>
                            <td className="p-0"></td>
                        </tr>
                        <tr>
                            <td className="p-0">
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tt("common.type")}:
                                    </div>
                                    <div className="col" style={styleValue}>
                                        {_Tc(`route_types.${formData.type}`)}
                                    </div>
                                </div>
                            </td>
                            <td className="p-0">
                                <div className="row">
                                    <div className="col small text-secondary" style={styleLabel}>
                                        {_Tr("labels.schedule")}:
                                    </div>
                                    <div className="col" style={styleValue}>
                                        <DurationDisplay seconds={formData.hour_start} format="HH:mm" />
                                        <span className="ms-2 me-2">{_Tt("programming.to")}</span>
                                        <DurationDisplay seconds={formData.hour_end} format="HH:mm" />
                                        <span className="ms-2 me-2">- {_Tt("programming.hours")}:</span>
                                        <DurationDisplay seconds={formData.duration} format="HH:mm" />
                                    </div>
                                </div>
                            </td>
                            <td className="pt-0 ps-0 pb-0 pe-3 text-end">
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    disabled={!hasChanged}
                                    onClick={onClickCalculate}>
                                    {_Tf("routes.calculate")}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
export default FormRouteInfo;
